import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import _ from 'lodash'
import { GitMerge, Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import { MultiSelect } from '@/components/MultiSelect'
import { Select } from '@/components/Select'
import { getEntities } from '@/services/entities.service'
import { deleteUser as deleteUserAccount, getUsers } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { ACTIVE_USER_STATUS_OPTIONS, ROLE_OPTIONS } from '@/utils/constants'
import { cn, hasAnyFilterSelected, toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import AddEditUserSlideout from './AddEditUserSlideout'
import MergeUserDialog from './MergeUserDialog'

const DEFAULT_FILTERS = {
  entity: [],
  role: [],
  hasLoggedIn: null,
}

const Users = observer(() => {
  // State
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [sorting, setSorting] = useState([{ id: 'last_name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [showUserSlideout, setShowUserSlideout] = useState(false)
  const [editUser, setEditUser] = useState(null)
  const [mergeUser, setMergeUser] = useState(null)

  // Context
  const { user } = useContext(UserStoreContext)

  // Filter or search changes shrink the result set, so deep pages can 404 from
  // the paginator. Reset to the first page in the same handler as the filter
  // change so React 18 batches both updates into a single render.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const queryKey = ['admin', 'users', sorting, pagination, searchTerm, filters]

  // GET entities
  const { data: entities } = useQuery({
    queryKey: ['admin', 'entities'],
    queryFn: () => getEntities('', { pageIndex: 0, pageSize: 99999 }),
  })

  // GET users
  const { data: users, isPending } = useQuery({
    queryKey,
    queryFn: () =>
      getUsers({
        sorting,
        pagination,
        searchTerm,
        filters: {
          entity: filters.entity || DEFAULT_FILTERS.entity,
          role: filters.role || DEFAULT_FILTERS.role,
          has_logged_in: filters.hasLoggedIn,
        },
      }),
    placeholderData: keepPreviousData,
  })

  // DELETE user
  const { isPending: pendingDelete, mutate: deleteUser } = useMutation({
    mutationFn: (data) => deleteUserAccount(data.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('User deleted successfully')

      // Refresh admin users list
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  return (
    <Layout breadcrumbs={['Admin', 'Users']}>
      <Layout.Heading title="Users" />

      {/* @fixme: This is a hacky way to make the table filters responsive */}
      <div className="flex flex-row items-center gap-2">
        {(searchTerm || hasAnyFilterSelected(filters)) && (
          <Button
            className="visible md:hidden"
            variant="outline"
            onClick={() => {
              setSearch('')
              setSearchTerm('')
              setFilters(DEFAULT_FILTERS)
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
        <Button className="visible md:hidden" onClick={() => setShowUserSlideout(true)}>
          Add User
        </Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            placeholder="Search for User"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          <MultiSelect
            placeholder="Filter by Account"
            options={
              entities?.results ? entities.results.map((e) => ({ ...e, label: e.name })) : []
            }
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                entity: value || [],
              }))
              resetToFirstPage()
            }}
            value={filters.entity}
            width="w-full md:w-56"
          />

          <MultiSelect
            placeholder="Filter by Role"
            options={ROLE_OPTIONS}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                role: value || [],
              }))
              resetToFirstPage()
            }}
            value={filters.role}
            width="w-full md:w-56"
          />

          <Select
            className="w-full md:w-56"
            placeholder="Filter by Active Users"
            options={ACTIVE_USER_STATUS_OPTIONS}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                hasLoggedIn: value,
              }))
              resetToFirstPage()
            }}
            value={filters.hasLoggedIn}
          />

          {(searchTerm || hasAnyFilterSelected(filters)) && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setFilters(DEFAULT_FILTERS)
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button className="hidden md:block" onClick={() => setShowUserSlideout(true)}>
          Add User
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorFn: (row) => formatUserName(row),
            id: 'last_name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className={`cursor-pointer text-left hover:underline ${!row.original.lastLogin ? 'text-muted-foreground' : 'text-action'}`}
                onClick={() => {
                  setEditUser(row.original)
                  setShowUserSlideout(true)
                }}
              >
                {formatUserName(row.original)}
              </button>
            ),
          },
          {
            accessorKey: 'email',
            header: ({ column }) => <SortableColumnHeader column={column} label="Email" />,
            cell: ({ row }) => (
              <span className={!row.original.lastLogin ? 'text-muted-foreground' : ''}>
                {row.original.email}
              </span>
            ),
          },
          {
            accessorKey: 'isXverseAdmin',
            id: 'is_xverse_admin',
            header: ({ column }) => <SortableColumnHeader column={column} label="Admin" />,
            cell: ({ row }) => (
              <span
                className={cn(
                  'mx-auto rounded-full px-2 py-1 text-xs uppercase',
                  row.original.isXverseAdmin
                    ? 'bg-primary text-white'
                    : 'bg-muted-foreground text-white',
                )}
              >
                {row.original.isXverseAdmin ? 'Yes' : 'No'}
              </span>
            ),
            size: 100,
          },
          {
            accessorKey: 'isXverseInstructor',
            id: 'is_xverse_instructor',
            header: ({ column }) => <SortableColumnHeader column={column} label="Instructor" />,
            cell: ({ row }) => (
              <span
                className={cn(
                  'mx-auto rounded-full px-2 py-1 text-xs uppercase',
                  row.original.isXverseInstructor
                    ? 'bg-primary text-white'
                    : 'bg-muted-foreground text-white',
                )}
              >
                {row.original.isXverseInstructor ? 'Yes' : 'No'}
              </span>
            ),
            size: 160,
          },
          {
            accessorKey: 'isXverseInstructorCandidate',
            id: 'is_xverse_instructor_candidate',
            header: ({ column }) => <SortableColumnHeader column={column} label="Candidate" />,
            cell: ({ row }) => (
              <span
                className={cn(
                  'mx-auto rounded-full px-2 py-1 text-xs uppercase',
                  row.original.isXverseInstructorCandidate
                    ? 'bg-primary text-white'
                    : 'bg-muted-foreground text-white',
                )}
              >
                {row.original.isXverseInstructorCandidate ? 'Yes' : 'No'}
              </span>
            ),
            size: 160,
          },
          {
            accessorKey: 'last_login',
            header: ({ column }) => <SortableColumnHeader column={column} label="Last Login" />,
            cell: ({ row }) => (
              <span>
                {row.original.lastLogin
                  ? dayjs(row.original.lastLogin).format('MMM D, YYYY h:mm A')
                  : 'N/A'}
              </span>
            ),
          },
          {
            accessorKey: 'last_activity',
            header: ({ column }) => <SortableColumnHeader column={column} label="Last Activity" />,
            cell: ({ row }) => (
              <span>
                {row.original.lastActivity
                  ? dayjs(row.original.lastActivity).format('MMM D, YYYY h:mm A')
                  : 'N/A'}
              </span>
            ),
          },
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditUser(row.original)
                  setShowUserSlideout(true)
                }}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
            ),
            size: 80,
          },
          {
            id: 'merge',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                variant="outline"
                size="sm"
                disabled={row.original.id === user?.id}
                onClick={() => setMergeUser(row.original)}
              >
                <GitMerge className="h-4 w-4" />
                <span className="sr-only">{`Merge ${formatUserName(row.original)}`}</span>
              </Button>
            ),
            size: 80,
          },
          {
            id: 'delete',
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Are you sure you want to delete: ${formatUserName(row.original)}?`}
                confirmTitle="Delete User"
                className="place-self-end"
                variant="destructive"
                size="sm"
                loading={pendingDelete}
                disabled={row.original.id === user?.id}
                onClick={() => deleteUser(row.original)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">{`Delete ${formatUserName(row.original)}`}</span>
              </ConfirmButton>
            ),
            size: 80,
          },
        ]}
        data={users?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={users?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {mergeUser && (
        <MergeUserDialog
          duplicate={mergeUser}
          onClose={() => setMergeUser(null)}
          onMerged={() => {
            setMergeUser(null)
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
          }}
        />
      )}

      {showUserSlideout && (
        <AddEditUserSlideout
          editUser={editUser}
          entities={entities?.results || []}
          queryKey={queryKey}
          resetAndClose={() => {
            setShowUserSlideout(false)
            setEditUser(null)
            queryClient.invalidateQueries({ queryKey: ['users'] })
          }}
        />
      )}
    </Layout>
  )
})

export default Users
